// Shared helpers to normalize each country's raw package data into ONE canonical
// package shape. The canonical shape is a SUPERSET — it keeps every field the
// existing consumers read (name, id, duration, price string, highlights,
// destination) and adds normalized fields for the hub + AI planner:
//   priceFrom (number|null), currency, slug, durationDays (number|null), regions.

export const slugify = (s = '') =>
  s
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')      // drop parenthetical suffixes e.g. "(Classic)"
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

// "9 Days / 8 Nights" | "3 days" | {days,nights} → number of days | null
export const durationToDays = (duration) => {
  if (duration && typeof duration === 'object') return duration.days ?? null;
  const m = String(duration || '').match(/(\d+)\s*day/i);
  return m ? Number(m[1]) : null;
};

// "9 Days / 8 Nights" (unchanged) | {days,nights} → "3 Days / 2 Nights"
export const durationLabel = (duration) => {
  if (duration && typeof duration === 'object') {
    const { days, nights } = duration;
    if (days != null && nights != null) return `${days} Days / ${nights} Nights`;
    if (days != null) return `${days} Days`;
    return '';
  }
  return duration || '';
};

// number | "$1,899" | "From $2,715" → number | null
export const toNumber = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const m = v.replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? Number(m[1]) : null;
  }
  return null;
};

// number|null → "From $2,715" | "Contact for pricing"
export const priceLabel = (n, currency = 'USD') => {
  if (n == null) return 'Contact for pricing';
  const symbol = currency === 'USD' ? '$' : '';
  return `From ${symbol}${Math.round(n).toLocaleString()}`;
};

// Lowest sharedPrice across a Kenya-style seasons array → number | null
export const minSeasonPrice = (seasons = []) => {
  const prices = (seasons || []).map((s) => s.sharedPrice).filter((p) => typeof p === 'number');
  return prices.length ? Math.min(...prices) : null;
};

// Every country stores day-by-day differently:
//   Kenya    { day: "Day 01", title, description }
//   Tanzania { day: 1, title, location, board, activity, description }
//   Rwanda   { day: 1, location, activities: [...] }        (no title/description)
//   SA       { day: 1, title, activities: "..." }
//   Zanzibar (none)
// Normalize all to { day, title, description } for the shared package page.
export const normalizeItinerary = (raw = []) =>
  (raw || []).map((d) => ({
    day:
      typeof d.day === 'number'
        ? `Day ${d.day}`
        : typeof d.day === 'string'
          ? d.day.replace(/^Day\s*0?/i, 'Day ')
          : '',
    title: d.title || d.location || '',
    description:
      d.description ||
      (Array.isArray(d.activities) ? d.activities.join(' · ') : d.activities) ||
      '',
  }));

// Build a canonical package from the pieces every adapter computes.
export const canonicalPackage = ({
  id,
  name,
  countryName,
  duration,
  priceFrom = null,
  currency = 'USD',
  description = '',
  highlights = [],
  regions = [],
  coverImage,
  rating = null,
  difficulty,
  featured = false,
  category,
  itinerary = [],
  seasons = null,
}) => ({
  id: String(id),
  slug: slugify(name),
  name,
  destination: countryName,           // consumed by TravelAIHelpers
  duration: durationLabel(duration),
  durationDays: durationToDays(duration),
  priceFrom,
  currency,
  price: priceLabel(priceFrom, currency), // string — never undefined (dataService/itinerary rely on this)
  description,
  highlights,
  regions,
  coverImage,
  rating,
  difficulty,
  featured,
  category,
  itinerary: normalizeItinerary(itinerary),
  seasons: seasons || null,
});
